import { Trans } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const imagePathSecondary = "/large.jpg";

function Top() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.6], [0.25, 0.6]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <div ref={containerRef} className="relative w-full h-screen p-3 md:p-5">
      {/* Clipping container with rounded corners */}
      <div className="relative w-full h-full rounded-2xl md:rounded-3xl overflow-hidden">
        {/* Parallax background image */}
        <motion.img
          src={imagePathSecondary}
          alt="App preview"
          className="absolute inset-0 w-full h-full object-cover will-change-transform"
          style={{ scale: imageScale, y: imageY }}
          draggable={false}
        />

        {/* Gradient overlay for depth */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: overlayOpacity,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.8) 100%)",
          }}
        />

        {/* Bottom-left text overlay with staggered reveal */}
        <motion.div
          className="absolute bottom-24 md:bottom-28 left-0 text-left px-6 md:px-12"
          style={{ y: textY }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.9,
              delay: 0.2,
              ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
            }}
            className="text-white drop-shadow-lg text-3xl md:text-7xl font-gnarly-semibold text-left leading-[1.1]"
          >
            <Trans i18nKey="app_title">
              Find shared flats <br /> you'll love coming back to.
            </Trans>
          </motion.h1>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          >
            <ArrowDown className="w-5 h-5 text-white/60" />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default Top;
